import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  LoadingState,
  Pagination,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createBusinessesApi } from "../api";
import {
  businessStatusTone,
  formatLabel,
  verificationStatusTone,
} from "../labels";
import type {
  BusinessListItemDto,
  DocumentsAwaitingReviewDto,
  BusinessStatus,
  BusinessTypeDto,
  VerificationStatus,
  VisibilityStatus,
} from "../types";
import { CreateBusinessDialog } from "../components/CreateAndProfileDialogs";
import { useLiveRefresh } from "@/lib/useLiveRefresh";

const STATUSES: BusinessStatus[] = [
  "draft",
  "applied",
  "under_review",
  "more_information_required",
  "verified",
  "active",
  "restricted",
  "suspended",
  "rejected",
  "closed",
];

const VERIFICATION: VerificationStatus[] = [
  "not_started",
  "requested",
  "submitted",
  "under_review",
  "more_information_required",
  "verified",
  "accepted",
  "rejected",
  "superseded",
  "expired",
  "revoked",
];

const VISIBILITY: VisibilityStatus[] = ["visible", "hidden", "paused", "restricted"];

/**
 * The document review queue as a view of this page.
 *
 * "Awaiting review" swaps the paged directory for the queue the API serves —
 * every business with a submitted document nobody has decided — because a
 * filter applied to one page of 25 would silently miss the rest.
 */
const QUEUE_SORTS = [
  { value: "oldest", label: "Waiting longest" },
  { value: "most", label: "Most documents" },
];

function formatWaiting(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const SORT_OPTIONS = [
  { value: "display_name", label: "Display name (A–Z)" },
  { value: "-display_name", label: "Display name (Z–A)" },
  { value: "status", label: "Status" },
  { value: "public_id", label: "Public ID" },
  { value: "id", label: "ID" },
];

export function BusinessesDirectoryPage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Business Center" },
  ]);

  const client = useApiClient();
  const api = useMemo(() => createBusinessesApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") ?? "";
  const status = (searchParams.get("status") ?? "") as BusinessStatus | "";
  const verificationStatus = (searchParams.get("verification_status") ??
    "") as VerificationStatus | "";
  const visibilityStatus = (searchParams.get("visibility_status") ??
    "") as VisibilityStatus | "";
  const businessTypeId = searchParams.get("business_type_id") ?? "";
  const sort = searchParams.get("sort") ?? "display_name";
  const cursor = searchParams.get("cursor") ?? undefined;
  const documentsView = searchParams.get("documents") === "awaiting" ? "awaiting" : "";
  const queueSort = searchParams.get("queue_sort") === "most" ? "most" : "oldest";

  const [rows, setRows] = useState<BusinessListItemDto[]>([]);
  const [types, setTypes] = useState<BusinessTypeDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(q);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  /**
   * The review queue. `null` while unknown; `"denied"` for someone without
   * `verification:view` — the column and filter then disappear rather than
   * showing zeroes that would read as "nothing to review".
   */
  const [queue, setQueue] = useState<DocumentsAwaitingReviewDto[] | "denied" | null>(null);
  const [queueError, setQueueError] = useState<string | null>(null);

  const page = cursorStack.length + 1;

  /**
   * `silent` is what makes background refresh bearable.
   *
   * Without it every poll ran setLoading(true), which swapped the table for a
   * spinner and back — the page appeared to reload itself on a timer, losing
   * scroll position and whatever the reviewer was reading. A refresh nobody
   * asked for must be invisible until it has something different to show.
   */
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const [list, typeList] = await Promise.all([
        api.list({
          ...(cursor ? { cursor } : {}),
          limit: 25,
          sort,
          ...(q ? { q } : {}),
          ...(status ? { status } : {}),
          ...(verificationStatus
            ? { verification_status: verificationStatus }
            : {}),
          ...(visibilityStatus ? { visibility_status: visibilityStatus } : {}),
          ...(businessTypeId ? { business_type_id: businessTypeId } : {}),
        }),
        api.listTypes(),
      ]);
      setRows(list.businesses);
      setNextCursor(list.pagination.next_cursor);
      setTypes(typeList.business_types);
    } catch (err) {
      setError(apiErrorMessage(err));
      // A failed BACKGROUND refresh must not empty a table that is being read.
      // The rows on screen are the last thing known to be true.
      if (!opts?.silent) {
        setRows([]);
        setNextCursor(undefined);
      }
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [
    api,
    cursor,
    sort,
    q,
    status,
    verificationStatus,
    visibilityStatus,
    businessTypeId,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadQueue = useCallback(async () => {
    try {
      const res = await api.listDocumentsAwaitingReview();
      setQueue(res.businesses);
      setQueueError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setQueue("denied");
      else setQueueError(apiErrorMessage(err));
    }
  }, [api]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const awaitingById = useMemo(() => {
    const map = new Map<string, number>();
    if (Array.isArray(queue)) for (const q of queue) map.set(q.business_id, q.documents_awaiting_review);
    return map;
  }, [queue]);

  const queueRows = useMemo(() => {
    if (!Array.isArray(queue)) return [];
    const rowsCopy = [...queue];
    if (queueSort === "most") {
      rowsCopy.sort(
        (a, b) =>
          b.documents_awaiting_review - a.documents_awaiting_review ||
          (a.oldest_submitted_at ?? "").localeCompare(b.oldest_submitted_at ?? ""),
      );
    }
    return rowsCopy;
  }, [queue, queueSort]);

  const canSeeQueue = queue !== "denied";

  /**
   * New applications arrive while the queue is open. A reviewer watching this
   * page should see one land without being told to press Refresh.
   */
  useLiveRefresh(() => {
    void load({ silent: true });
    void loadQueue();
  });

  useEffect(() => {
    setSearchDraft(q);
  }, [q]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  function applySearch() {
    setCursorStack([]);
    updateParams({ q: searchDraft.trim() || undefined, cursor: undefined });
  }

  function goNext() {
    if (!nextCursor) return;
    setCursorStack((stack) => [...stack, cursor ?? ""]);
    updateParams({ cursor: nextCursor });
  }

  function goPrevious() {
    if (cursorStack.length === 0) return;
    const stack = [...cursorStack];
    const previous = stack.pop();
    setCursorStack(stack);
    updateParams({
      cursor: previous && previous.length > 0 ? previous : undefined,
    });
  }

  const typeName = (id: string) =>
    types.find((type) => type.id === id)?.name ?? id;

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Business Center"
        description="Operate Business supply lifecycle. Business Type is behavior; Marketplace Category is grouping."
        actions={
          <Flex gap={8} wrap>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/businesses/types")}
            >
              Business Types
            </Button>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/businesses/skills")}
            >
              Freelancer skills
            </Button>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/businesses/resources")}
            >
              Bookable Resources
            </Button>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/businesses/credentials")}
            >
              Credential review
            </Button>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/businesses/cancelled")}
            >
              Cancelled Applications
            </Button>
            <Button variant="secondary" onClick={() => setCreateOpen(true)}>
              Create Business
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      <Filters>
        <div style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Search
            value={searchDraft}
            onValueChange={setSearchDraft}
            placeholder="Search name or public ID"
            onKeyDown={(event) => {
              if (event.key === "Enter") applySearch();
            }}
          />
        </div>
        <Select
          label="Status"
          value={status}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All statuses" },
            ...STATUSES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Verification"
          value={verificationStatus}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              verification_status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All verification" },
            ...VERIFICATION.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Visibility"
          value={visibilityStatus}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              visibility_status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All visibility" },
            ...VISIBILITY.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Business Type"
          value={businessTypeId}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              business_type_id: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All types" },
            ...types.map((type) => ({
              value: type.id,
              label: type.name,
            })),
          ]}
        />
        {canSeeQueue ? (
          <Select
            label="Documents"
            value={documentsView}
            onChange={(event) => {
              setCursorStack([]);
              updateParams({ documents: event.target.value || undefined, cursor: undefined });
            }}
            options={[
              { value: "", label: "All businesses" },
              {
                value: "awaiting",
                label: `Awaiting review${Array.isArray(queue) ? ` (${queue.length})` : ""}`,
              },
            ]}
          />
        ) : null}
        <Select
          label="Sort"
          value={sort}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ sort: event.target.value, cursor: undefined });
          }}
          options={SORT_OPTIONS}
        />
        <Button variant="primary" onClick={applySearch}>
          Apply search
        </Button>
      </Filters>

      {queueError ? (
        <Alert tone="warning" title="Document review counts unavailable">
          <Flex gap={8} align="center" wrap>
            <Text role="small" as="span">{queueError}</Text>
            <Button size="sm" variant="secondary" onClick={() => void loadQueue()}>
              Retry
            </Button>
          </Flex>
        </Alert>
      ) : null}

      {documentsView === "awaiting" ? (
        queue === null && !queueError ? (
          <LoadingState message="Loading the review queue…" />
        ) : queue === "denied" ? (
          <EmptyState
            reason="scope"
            title="You cannot see the document review queue"
            description="It needs the verification view permission. Ask an administrator to grant it to your access assignment."
          />
        ) : queueRows.length === 0 ? (
          queueError ? null : (
            <EmptyState
              reason="none"
              title="No documents awaiting review"
              description="Every submitted document has been accepted or rejected. New uploads from the HUDDL app appear here as they finish."
              action={
                <Button variant="secondary" onClick={() => updateParams({ documents: undefined })}>
                  Show all businesses
                </Button>
              }
            />
          )
        ) : (
          <Stack gap={12}>
            <ActionBar
              left={
                <Text role="small" color="secondary" as="span">
                  {queueRows.length} business{queueRows.length === 1 ? "" : "es"} with documents
                  awaiting review
                </Text>
              }
              right={
                <Select
                  label="Order"
                  value={queueSort}
                  onChange={(event) => updateParams({ queue_sort: event.target.value })}
                  options={QUEUE_SORTS}
                />
              }
            />
            <Table>
              <Thead>
                <Tr>
                  <Th>Business</Th>
                  <Th>Status</Th>
                  <Th>Documents to review</Th>
                  <Th>Waiting since</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {queueRows.map((row) => (
                  <Tr key={row.business_id}>
                    <Td>
                      <Text role="bodyStrong" as="div">{row.display_name}</Text>
                    </Td>
                    <Td>
                      <StatusChip tone={businessStatusTone(row.status)}>
                        {formatLabel(row.status)}
                      </StatusChip>
                    </Td>
                    <Td>
                      <StatusChip tone="warning">{row.documents_awaiting_review}</StatusChip>
                    </Td>
                    <Td>{formatWaiting(row.oldest_submitted_at)}</Td>
                    <Td>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => void navigate(`/app/businesses/${row.business_id}`)}
                      >
                        Review
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Stack>
        )
      ) : null}

      {documentsView !== "awaiting" && error ? (
        <Alert tone="danger" title="Unable to load Businesses">
          {error}
        </Alert>
      ) : null}

      {documentsView !== "awaiting" && loading ? <LoadingState message="Loading Businesses…" /> : null}

      {documentsView !== "awaiting" && !loading && !error && rows.length === 0 ? (
        <EmptyState
          reason={q || status || verificationStatus || businessTypeId ? "filters" : "none"}
          title="No Businesses found"
          action={
            <Button
              variant="secondary"
              onClick={() => {
                setCursorStack([]);
                setSearchParams(new URLSearchParams());
              }}
            >
              Clear filters
            </Button>
          }
        />
      ) : null}

      {documentsView !== "awaiting" && !loading && rows.length > 0 ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Showing {rows.length} Business{rows.length === 1 ? "" : "es"}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) goNext();
                  if (nextPage < page) goPrevious();
                }}
              />
            }
          />
          <Table>
            <Thead>
              <Tr>
                <Th>Business</Th>
                <Th>Owner</Th>
                <Th>Type</Th>
                <Th>Sports</Th>
                <Th>Listings</Th>
                <Th>Bookings</Th>
                <Th>Status</Th>
                <Th>Verification</Th>
                <Th>Visibility</Th>
                {canSeeQueue ? <Th>Docs to review</Th> : null}
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    <Text role="bodyStrong" as="div">
                      {row.display_name}
                    </Text>
                    <Text role="caption" color="muted" as="div">
                      {row.legal_name} · {row.public_id}
                    </Text>
                    {row.summary?.market_name ? (
                      <Text role="caption" color="muted" as="div">
                        {row.summary.market_name}
                      </Text>
                    ) : null}
                  </Td>
                  <Td>
                    {row.summary?.owner ? (
                      <>
                        <Text role="small" as="div">
                          {row.summary.owner.name ?? "Unnamed"}
                        </Text>
                        <Text role="caption" color="muted" as="div">
                          {row.summary.owner.email ?? "—"}
                        </Text>
                      </>
                    ) : row.summary ? (
                      <Text role="small" color="muted" as="span">No owner</Text>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>{typeName(row.business_type_id)}</Td>
                  <Td>
                    {row.summary && row.summary.sports.length ? row.summary.sports.join(", ") : "—"}
                  </Td>
                  <Td>
                    {row.summary
                      ? `${row.summary.resources_live}/${row.summary.resources_total} live`
                      : "—"}
                  </Td>
                  <Td>{row.summary ? row.summary.bookings_total : "—"}</Td>
                  <Td>
                    <StatusChip tone={businessStatusTone(row.status)}>
                      {formatLabel(row.status)}
                    </StatusChip>
                  </Td>
                  <Td>
                    <StatusChip
                      tone={verificationStatusTone(row.verification_status)}
                    >
                      {formatLabel(row.verification_status)}
                    </StatusChip>
                  </Td>
                  <Td>{formatLabel(row.visibility_status)}</Td>
                  {canSeeQueue ? (
                    <Td>
                      {queueError ? (
                        <Text role="small" color="muted" as="span" title="The review queue could not be loaded">—</Text>
                      ) : queue === null ? (
                        "…"
                      ) : (awaitingById.get(row.id) ?? 0) > 0 ? (
                        <StatusChip tone="warning">{awaitingById.get(row.id)}</StatusChip>
                      ) : (
                        <Text role="small" color="muted" as="span">0</Text>
                      )}
                    </Td>
                  ) : null}
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => void navigate(`/app/businesses/${row.id}`)}
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Stack>
      ) : null}

      <CreateBusinessDialog
        open={createOpen}
        types={types}
        busy={createBusy}
        error={createError}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
        onSubmit={async (input) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.create(input);
            toast.push({
              title: "Business created",
              description: result.business.display_name,
              tone: "success",
            });
            setCreateOpen(false);
            void navigate(`/app/businesses/${result.business.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </Stack>
  );
}
